//DashBoardNonMedical.js
import React, { useState, useRef, useEffect } from 'react';
import { sendMessage } from '../Services/chatService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import {
  Send,
  Bot,
  User as UserIcon,
  Loader2,
  Sparkles,
  RotateCcw,
  Heart,
  Pill,
  Activity,
  MessageCircle,
  BookHeart,
  AlertTriangle,
} from 'lucide-react';
import { historyService } from '../Services/historyService';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ReactMarkdown from 'react-markdown';
import QuotaLimitModal from '../components/QuotaLimitModal';
import HealthDataConsent from '../components/HealthDataConsent';
import consentService from '../Services/consentService.js';
import { FREE_DAILY_SEARCHES } from '../config/quotas';
import { useNavigate } from 'react-router-dom';

const DashboardNonMedical = () => {
  const { user, updateUser, hasPremiumAccess } = useAuth();

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Bonjour **${user?.first_name}** ! 👋\n\nJe suis **Remadia**, votre assistant santé personnel. Je suis là pour vous aider avec des explications simples et claires.\n\nQue puis-je faire pour vous aujourd'hui ? 🩺`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showHealthConsent, setShowHealthConsent] = useState(false);
  // null = non premium / en attente, 'connected' = profil rempli, 'empty' = profil vide
  const [healthProfileStatus, setHealthProfileStatus] = useState(null);
  const navigate = useNavigate();

  // 🆕 États pour le streaming simulé
  const [isTyping, setIsTyping] = useState(false);
  const [displayedContent, setDisplayedContent] = useState('');
  const [fullContent, setFullContent] = useState('');

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const checkConsent = async () => {
      if (user?.uid) {
        const hasConsent = await consentService.hasHealthDataConsent(user.uid);
        if (!hasConsent) {
          setShowHealthConsent(true);
        }
      }
    };

    checkConsent();
  }, [user]);

  useEffect(() => {
    const checkHealthProfile = async () => {
      if (!hasPremiumAccess) return;

      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return;

        const token = await firebaseUser.getIdToken();
        const response = await fetch(
          'https://europe-west1-medaviceia.cloudfunctions.net/getHealthProfileV2',
          { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) return;

        const data = await response.json();
        const profile = data.profile;

        const isFilled = profile &&
          typeof profile === 'object' &&
          Object.values(profile).some(
            (v) => v !== null && v !== '' && v !== undefined && !(Array.isArray(v) && v.length === 0)
          );

        setHealthProfileStatus(isFilled ? 'connected' : 'empty');
      } catch {
        // Silencieux — l'indicateur reste masqué en cas d'erreur réseau
      }
    };

    checkHealthProfile();
  }, [user, hasPremiumAccess]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    if (!user?.uid) {
      toast.error('Vous devez être connecté');
      return;
    }

    const userMessage = input.trim();
    setInput('');

    // Ajouter le message utilisateur
    const newUserMessage = { role: 'user', content: userMessage, timestamp: new Date() };
    setMessages((prev) => [...prev, newUserMessage]);

    setLoading(true);

    try {
      const data = await sendMessage(userMessage, messages, null, user.uid);
      const aiResponse = data.content;

      // 1. Mettre à jour les variables globales (au cas où tu en aies besoin ailleurs)
      setFullContent(aiResponse);
      setDisplayedContent(aiResponse);
      setIsTyping(false);

      // 2. CORRECTION : On enregistre le message DEFINITIF dans le tableau
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: aiResponse, // 👈 On sauvegarde la vraie réponse ici !
          timestamp: new Date(),
          isTyping: false, // 👈 On dit que l'IA a fini d'écrire pour figer le message
        },
      ]);

      // Quota check — l'essai n'est pas décompté (le serveur non plus : chatService.js)
      if (!hasPremiumAccess) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const newCount = userDoc.data().daily_search_count || 0;
          updateUser({ daily_search_count: newCount });

          const remaining = Math.max(0, FREE_DAILY_SEARCHES - newCount);
          if (remaining === 0) {
            toast.info(
              `Vous avez atteint votre limite quotidienne de ${FREE_DAILY_SEARCHES} recherches. Revenez demain ou passez à Premium.`,
              { duration: 3000 }
            );
          } else {
            toast.info(
              `${remaining} recherche${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''} aujourd'hui`,
              { duration: 3000 }
            );
          }
        }
      }

      // Historique
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userSettings = userDoc.data()?.settings || {};

        if (userSettings.saveHistory !== false) {
          await historyService.saveConversation(user.uid, userMessage, aiResponse);
        }
      } catch (historyError) {
        console.warn('Historique non sauvegardé:', historyError);
      }
    } catch (error) {
      console.error('Erreur chat:', error);

      if (error.message === 'QUOTA_EXCEEDED') {
        setShowQuotaModal(true);
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages([
      {
        role: 'assistant',
        content: `Nouvelle conversation ! 👋\n\nComment puis-je vous aider aujourd'hui ? 🩺`,
        timestamp: new Date(),
      },
    ]);
  };

  const markdownComponents = {
    h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
    h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-4 mb-2" {...props} />,
    h3: ({ node, ...props }) => <h3 className="text-base font-bold mt-3 mb-2" {...props} />,
    p: ({ node, ...props }) => (
      <p className="mb-3 leading-relaxed last:mb-0" style={{ whiteSpace: 'pre-line' }} {...props} />
    ),
    ul: ({ node, ...props }) => <ul className="list-none pl-0 mb-4 space-y-1.5" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4 space-y-1.5" {...props} />,
    li: ({ node, children, ...props }) => (
      <li className="flex items-start gap-2.5" {...props}>
        <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex-shrink-0 shadow-sm" />
        <span className="flex-1">{children}</span>
      </li>
    ),
    strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
    blockquote: ({ node, ...props }) => (
      <blockquote className="border-l-4 border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 pl-4 pr-2 py-1 italic my-4 rounded-r-lg" {...props} />
    ),
  };

  // Signalétique d'urgence : colore UNIQUEMENT les numéros 15 / 112 en rouge.
  // \b(?:15|112)\b = tokens isolés seulement (ne touche pas 150, 1120, "15mg"…).
  const colorEmergencyNumbers = (children) =>
    React.Children.map(children, (child) => {
      if (typeof child !== 'string') return child;
      return child.split(/(\b(?:15|112)\b)/g).map((part, i) =>
        /^(?:15|112)$/.test(part) ? (
          <span key={i} style={{ color: '#DC2626', fontWeight: 700 }}>
            {part}
          </span>
        ) : (
          part
        )
      );
    });

  // Variante des composants markdown appliquée SEULEMENT à la réponse d'urgence
  // (détectée par le sentinel "15 (SAMU)") → aucune réponse normale n'est colorée.
  const emergencyMarkdownComponents = {
    ...markdownComponents,
    p: ({ node, children, ...props }) => (
      <p className="mb-3 leading-relaxed last:mb-0" style={{ whiteSpace: 'pre-line' }} {...props}>
        {colorEmergencyNumbers(children)}
      </p>
    ),
    strong: ({ node, children, ...props }) => (
      <strong className="font-bold" {...props}>
        {colorEmergencyNumbers(children)}
      </strong>
    ),
  };

  const EMERGENCY_SENTINEL = '15 (SAMU)';

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header simplifié */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Bonjour {user?.first_name} ! 👋
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">
                  Votre assistant santé personnel
                </p>

                {healthProfileStatus === 'connected' && (
                  <span className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: '#10B981' }}>
                    <BookHeart className="w-3 h-3" />
                    Carnet santé connecté ✓
                  </span>
                )}

                {healthProfileStatus === 'empty' && (
                  <button
                    onClick={() => navigate('/health-record')}
                    className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-medium text-white transition-opacity hover:opacity-80"
                    style={{ backgroundColor: '#F59E0B' }}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Carnet non connecté — Finaliser mon profil
                  </button>
                )}
              </div>
            </div>

            <Button
              onClick={resetConversation}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-2 border-blue-500 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-semibold"
            >
              <RotateCcw className="w-4 h-4" />
              Nouvelle conversation
            </Button>
          </div>
        </div>

        {/* Chat Container */}
        <Card className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Messages Area */}
          <div className="h-[calc(100vh-320px)] overflow-y-auto px-6 py-6 bg-gradient-to-b from-blue-50/30 to-white dark:from-gray-900 dark:to-gray-950">
            {/* Message d'accueil si vide */}
            {messages.length === 1 && (
              <div className="mb-8">
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                  {[
                    {
                      text: 'Comment soulager un mal de tête ?',
                      icon: '🤕',
                      color: 'from-blue-400 to-blue-500',
                    },
                    {
                      text: 'Que faire en cas de fièvre ?',
                      icon: '🌡️',
                      color: 'from-orange-400 to-orange-500',
                    },
                    {
                      text: 'Analyse mes dernières mesures de santé',
                      icon: '📊',
                      color: 'from-emerald-400 to-emerald-500',
                    },
                    {
                      text: 'Mon profil santé présente-t-il des risques ?',
                      icon: '🩺',
                      color: 'from-violet-400 to-violet-500',
                    },
                  ].map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(suggestion.text)}
                      className="group p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 rounded-xl text-left transition-all border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 bg-gradient-to-br ${suggestion.color} rounded-lg flex items-center justify-center text-2xl`}
                        >
                          {suggestion.icon}
                        </div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                          {suggestion.text}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 mb-6 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-white p-1">
                    <img
                      src="/logoR2.png"
                      alt="Remadia"
                      className="w-full h-full object-contain scale-150"
                    />
                  </div>
                )}

                <div className={`max-w-[80%] ${message.role === 'user' ? 'order-1' : ''}`}>
                  <div
                    className={`rounded-2xl px-5 py-4 shadow-sm ${
                      message.role === 'user'
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                        : 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-blue-950/60 text-gray-900 dark:text-white border border-blue-200/70 dark:border-blue-800/50 shadow-md'
                    }`}
                  >
                    {/* 🆕 Afficher le contenu en cours de typing ou le contenu final */}
                    {message.isTyping ? (
                      <div>
                        <ReactMarkdown
                          components={
                            typeof displayedContent === 'string' &&
                            displayedContent.includes(EMERGENCY_SENTINEL)
                              ? emergencyMarkdownComponents
                              : markdownComponents
                          }
                        >
                          {displayedContent}
                        </ReactMarkdown>
                        {/*
                        {/* Curseur clignotant */}
                        <span className="inline-block w-2 h-5 bg-blue-500 animate-pulse ml-0.5 align-middle" />
                      </div>
                    ) : (
                      <ReactMarkdown
                        components={
                          typeof message.content === 'string' &&
                          message.content.includes(EMERGENCY_SENTINEL)
                            ? emergencyMarkdownComponents
                            : markdownComponents
                        }
                      >
                        {typeof message.content === 'string'
                          ? message.content
                          : message.content.text || message.content.content || ''}
                      </ReactMarkdown>
                    )}
                  </div>

                  {!message.isTyping && (
                    <div
                      className={`text-xs text-gray-400 dark:text-gray-500 mt-1.5 px-2 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
                    >
                      {message.timestamp?.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  )}
                </div>

                {message.role === 'user' && (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center flex-shrink-0 shadow-md order-2">
                    <UserIcon className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
            ))}

            {loading && !isTyping && (
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-white">
                  <img
                    src="/logoR2.png"
                    alt="Remadia"
                    className="w-full h-full object-contain scale-125"
                  />
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <div
                        className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      />
                      <div
                        className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      />
                      <div
                        className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      />
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      Je réfléchis...
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t-2 border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <form onSubmit={handleSend} className="flex gap-3 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                  placeholder="Posez votre question ici..."
                  rows="1"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
                  style={{ maxHeight: '120px', minHeight: '48px' }}
                  disabled={loading || isTyping}
                />
              </div>

              <Button
                type="submit"
                disabled={!input.trim() || loading || isTyping}
                className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl disabled:opacity-50 shadow-lg flex-shrink-0"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>

            <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-full border border-amber-200 dark:border-amber-800">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                Réponses simplifiées
              </span>
              <span>•</span>
              <span>Les réponses sont générées par IA</span>
            </div>

          </div>
        </Card>

        {/* ✅ Popup consentement données santé */}
        {showHealthConsent && (
          <HealthDataConsent userId={user.uid} onClose={() => setShowHealthConsent(false)} />
        )}

        {/* Info card en bas */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          {/* ... reste du code ... */}
        </div>
      </div>

      {/* Modal quota */}
      {showQuotaModal && (
        <QuotaLimitModal
          isOpen={showQuotaModal}
          onClose={() => setShowQuotaModal(false)}
          onUpgrade={() => navigate('/pricing')}
        />
      )}
    </DashboardLayout>
  );
};

export default DashboardNonMedical;
